import { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import { ScrollView, View as RNView } from "react-native";
import { AiIconSm } from "@src/components/icons/AiIconSm";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { View, Text, FormInput, Button } from "@src/components";
import { useAgentQuery, useUpdateAlarmSettings, useAlarm } from "@src/services/api/hooks";
import type { ParsedAgentConfig, AlarmException, AlarmRoutine, ArmedResponse } from "@src/services/api/types";
import type { ExceptionDisplayItem } from "@src/features/alarms/utils/alarm-utils";
import { useTenantId } from "@src/features/tenants";
import { useCameraRouteParams } from "@src/hooks/useCameraRouteParams";
import { ResponsePreview } from "@src/features/alarms/components/ResponsePreview";
import { RoutinePreview } from "@src/features/alarms/components/RoutinePreview";
import { ExceptionsPreview } from "@src/features/alarms/components/ExceptionsPreview";
import { UnistylesRuntime, useUnistyles } from "react-native-unistyles";
import { universalConfirm } from "@src/components/UniversalAlert";
import { extractJsonFromText } from "@visioni/a3s-agent";
import { compareExceptionsForAgentDisplay, getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { MarkdownMessage } from "@src/components/MarkdownMessage";
import { TypingAnimation } from "@src/components/typing-animation";
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller";
import Animated, { useAnimatedStyle } from "react-native-reanimated";
import { track } from "@visioni/a3s-analytics";
import { cameraScope } from "@src/features/cameras/analytics";
import { AiAgentApplyAllClicked, AiAgentEntered, AiAgentMessageSent, AiAgentPreviewClicked } from "../analytics";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { Icon } from "@src/components/Icon";
import { showErrorToast } from "@src/utils/toast";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  parsedConfig?: ParsedAgentConfig; // Parsed configuration from assistant response
  applied?: boolean; // True when user has applied this message's suggestions
  /** Exceptions that were in the diff (added/modified) when user applied - shown after apply instead of full list */
  appliedExceptionItems?: ExceptionDisplayItem[];
};

const AnimatedView = Animated.createAnimatedComponent(RNView);

const AGENT_ERROR_MESSAGE = "Something went wrong. Please try again.";

type AgentScreenContentProps = {
  embeddedOverride?: boolean;
};

function AgentScreenContent({ embeddedOverride }: AgentScreenContentProps) {
  const router = useRouter();
  const navigation = useNavigation();
  const params = useLocalSearchParams<{ title?: string; embedded?: string }>();
  const tenantId = useTenantId();

  const { siteId, cameraKey } = useCameraRouteParams({
    requireSiteId: true,
    requireCameraKey: true,
  });

  const agentTitle = (params.title as string | undefined) || "AI Schedule Assistant";
  const isEmbedded = embeddedOverride ?? params.embedded === "1";
  const { theme } = useUnistyles();
  const { height } = useReanimatedKeyboardAnimation();
  const fallbackRoute = useMemo(() => {
    if (tenantId && siteId && cameraKey) {
      return `/tenants/${tenantId}/sites/${siteId}/cameras/${encodeURIComponent(cameraKey)}`;
    }
    return "/(tabs)/alarms";
  }, [tenantId, siteId, cameraKey]);
  const { headerLeft } = useHeaderBackButton(fallbackRoute);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: height.value }],
  }));

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content:
        "Hi! I can help you configure your alarm system. Tell me what changes you'd like to make in plain English.",
    },
  ]);
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const [isApplyingAll, setIsApplyingAll] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>("");
  const scrollViewRef = useRef<ScrollView>(null);

  const agentMutation = useAgentQuery();
  const updateSettingsMutation = useUpdateAlarmSettings();
  const { data: alarmDetail } = useAlarm(siteId, cameraKey);
  const timezone = useMemo(() => getTimezoneFromAlarmDetail(alarmDetail) || "UTC", [alarmDetail]);
  const existingAlarmExceptions = alarmDetail?.settings?.exceptions ?? [];

  useLayoutEffect(() => {
    if (isEmbedded) {
      navigation.setOptions({ headerShown: false });
      return;
    }
    navigation.setOptions({ headerShown: true, headerLeft });
  }, [headerLeft, isEmbedded, navigation]);

  // Track when entering AI agent
  useEffect(() => {
    track(AiAgentEntered, cameraScope(tenantId, siteId, cameraKey));
  }, [cameraKey, tenantId, siteId]); // Only track on mount

  const hasActionableSuggestions = (config: ParsedAgentConfig | undefined | null): boolean => {
    if (!config) return false;
    const hasResponse = config.response != null;
    const hasRoutine = config.routine != null;
    const hasExceptions = (config.exceptions?.length ?? 0) > 0;
    return hasResponse || hasRoutine || hasExceptions;
  };

  const handleApplyAll = async (config: ParsedAgentConfig | undefined, messageIndex: number) => {
    if (!config || isApplyingAll) return;

    track(AiAgentApplyAllClicked, {
      ...cameraScope(tenantId, siteId, cameraKey),
      has_response: !!config.response,
      has_routine: !!config.routine,
      exceptions_count: config.exceptions?.length ?? 0,
    });

    const confirmed = await universalConfirm({
      title: "Apply changes?",
      message: "Please confirm you've previewed and verified all the proposed changes.",
      confirmLabel: "Apply changes",
      cancelLabel: "Cancel",
    });

    if (!confirmed) return;

    setIsApplyingAll(true);
    try {
      await updateSettingsMutation.mutateAsync({
        siteId,
        cameraKey,
        body: {
          ...(config.response && { response: config.response }),
          ...(config.routine && { routine: config.routine }),
          ...(config.exceptions && config.exceptions.length > 0 && { exceptions: config.exceptions }),
          timezone,
        },
      });
      const diff = compareExceptionsForAgentDisplay(existingAlarmExceptions, config.exceptions);
      const actionableItems = diff.items.filter(
        (i): i is ExceptionDisplayItem => i.type === "added" || i.type === "modified",
      );
      setMessages((prev) =>
        prev.map((m, i) => (i === messageIndex ? { ...m, applied: true, appliedExceptionItems: actionableItems } : m)),
      );
    } catch (error) {
      console.error("Failed to apply all alarm settings:", error);
      showErrorToast({ error });
    } finally {
      setIsApplyingAll(false);
    }
  };

  // Build conversation history for API
  const buildHistory = (): Array<{ role: "user" | "assistant"; content: string }> => {
    return messages
      .filter((msg) => msg.role === "user" || msg.role === "assistant")
      .map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));
  };

  // Parse assistant response content (may be plain JSON or mixed prose + JSON)
  const parseAssistantContent = (content: string): ParsedAgentConfig | null => {
    try {
      const jsonStr = extractJsonFromText(content) ?? content;
      const parsed: unknown = JSON.parse(jsonStr);
      return parsed as ParsedAgentConfig;
    } catch {
      // Not JSON, return null
      return null;
    }
  };

  const handleSend = async () => {
    if (!draft.trim() || isThinking) return;

    const userMessage: ChatMessage = {
      role: "user",
      content: draft.trim(),
    };

    track(AiAgentMessageSent, {
      ...cameraScope(tenantId, siteId, cameraKey),
      message_length: draft.trim().length,
    });

    setMessages((prev) => [...prev, userMessage]);
    setDraft("");
    setIsThinking(true);

    try {
      const history = buildHistory();
      const response = await agentMutation.mutateAsync({
        siteId,
        cameraKey,
        request: {
          userQuery: draft.trim(),
          skill: "alarm_manager",
          history,
        },
      });

      // Find the last assistant message
      const lastAssistantMessage = response.messages
        .slice()
        .reverse()
        .find((msg) => msg.role === "assistant");

      if (lastAssistantMessage && typeof lastAssistantMessage.content === "string") {
        const parsedConfig = parseAssistantContent(lastAssistantMessage.content);
        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: lastAssistantMessage.content,
          parsedConfig: parsedConfig || undefined,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error("Agent query error:", error);
      const errorMessage: ChatMessage = {
        role: "assistant",
        content: AGENT_ERROR_MESSAGE,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleResponsePress = (response: ArmedResponse) => {
    track(AiAgentPreviewClicked, {
      ...cameraScope(tenantId, siteId, cameraKey),
      preview_type: "response",
    });
    // Pass the response state as URL parameters
    const urlParams: Record<string, string> = {
      recording: response.recording,
      monitoring: response.monitoring,
      sirens: response.sirens,
      strobes: response.strobes,
      tenantId,
      siteId,
    };
    const paramsString = new URLSearchParams(urlParams).toString();
    router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/response?${paramsString}`);
  };

  const handleRoutinePress = (routine: AlarmRoutine) => {
    track(AiAgentPreviewClicked, {
      ...cameraScope(tenantId, siteId, cameraKey),
      preview_type: "routine",
      routine_kind: routine.kind,
    });
    // Navigate to customize-routine with the proposed routine
    const routineJson = encodeURIComponent(JSON.stringify(routine));
    router.push(
      `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/customize-routine?routine=${routineJson}`,
    );
  };

  const handleExceptionPress = (exception: AlarmException) => {
    track(AiAgentPreviewClicked, {
      ...cameraScope(tenantId, siteId, cameraKey),
      preview_type: "exception",
    });
    const period = exception.period;

    // Convert exception data to URL parameters
    const urlParams: Record<string, string> = {
      reason: exception.reason,
      action: exception.action,
      tenantId,
      siteId,
    };

    urlParams.startDate = `${period.start.year}-${String(period.start.month).padStart(2, "0")}-${String(period.start.day).padStart(2, "0")}`;
    urlParams.startHour = String(period.start.hour);
    urlParams.startMinute = String(period.start.minute);

    urlParams.endDate = `${period.end.year}-${String(period.end.month).padStart(2, "0")}-${String(period.end.day).padStart(2, "0")}`;

    // Handle 24:00 special case
    const isEnd24Hour = period.end.hour === 24 && period.end.minute === 0;
    urlParams.endHour = String(isEnd24Hour ? 0 : period.end.hour);
    urlParams.endMinute = String(period.end.minute);
    urlParams.endIs24Hour = String(isEnd24Hour);

    // Build query string and navigate
    const paramsString = new URLSearchParams(urlParams).toString();
    router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exceptions/new?${paramsString}`);
  };

  useEffect(() => {
    // Scroll to bottom when messages change
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, [messages, isThinking]);

  return (
    <View flex={1} bg="background">
      <ScrollView
        ref={scrollViewRef}
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: 20,
          paddingBottom: 120,
          flexGrow: 1,
        }}
        keyboardShouldPersistTaps="handled"
      >
        {messages.map((msg, idx) => {
          const isUser = msg.role === "user";
          const label = isUser ? "You" : agentTitle;
          const config = msg.role === "assistant" ? msg.parsedConfig : undefined;
          const isApplied = msg.applied === true;
          const exceptionsResult =
            config?.exceptions ?
              isApplied && msg.appliedExceptionItems && msg.appliedExceptionItems.length > 0 ?
                { items: msg.appliedExceptionItems }
              : isApplied ? null
              : compareExceptionsForAgentDisplay(existingAlarmExceptions, config.exceptions)
            : null;
          const hasParsedConfig = !!config;
          const showExceptions = exceptionsResult && exceptionsResult.items.length > 0;
          const theme = UnistylesRuntime.getTheme();

          return (
            <View key={idx} alignItems={isUser ? "flex-end" : "flex-start"} mb="4xl">
              <View
                flexDirection="row"
                alignItems="center"
                gap="sm"
                mb="xs"
                style={{ alignSelf: isUser ? "flex-end" : "flex-start" }}
              >
                {isUser ?
                  <>
                    <Icon name="userCircle" size={20} color={theme.colors.icon} />
                    <Text textStyle="xs/medium" style={{ color: theme.colors.interactive.controls.default_midground }}>
                      {label}
                    </Text>
                  </>
                : <>
                    <AiIconSm size={28} />
                    <Text textStyle="xs/medium" style={{ color: theme.colors.interactive.controls.default_midground }}>
                      AI Assistant
                    </Text>
                  </>
                }
              </View>

              {/* Only show message content if there's no parsed config (to avoid showing raw JSON) */}
              {!hasParsedConfig && <MarkdownMessage content={msg.content} isUser={isUser} />}

              {/* Show parsed configuration changes */}
              {hasParsedConfig && (
                <View gap="md" style={{ width: "100%" }}>
                  <View gap="md">
                    {msg.parsedConfig!.response && (
                      <ResponsePreview
                        response={msg.parsedConfig!.response}
                        onPress={() => handleResponsePress(msg.parsedConfig!.response!)}
                        accessoryIcon="chevronRight"
                      />
                    )}
                    {msg.parsedConfig!.routine && (
                      <RoutinePreview
                        routine={msg.parsedConfig!.routine}
                        onPress={() => handleRoutinePress(msg.parsedConfig!.routine!)}
                        accessoryIcon="chevronRight"
                      />
                    )}
                    {showExceptions && exceptionsResult && (
                      <View>
                        <Text textStyle="md/semibold" fontWeight="600" mb="md">
                          Exceptions
                        </Text>
                        <ExceptionsPreview
                          exceptions={config?.exceptions ?? []}
                          displayItems={exceptionsResult.items}
                          timezone={timezone}
                          onExceptionPress={(id) => {
                            const ex = config?.exceptions?.find((e) => e.id === id);
                            if (ex) handleExceptionPress(ex);
                          }}
                          accessoryIcon="chevronRight"
                        />
                      </View>
                    )}
                  </View>
                  {isApplied ?
                    <Text textStyle="sm/regular" color="textMuted" mt="md" style={{ fontStyle: "italic" }}>
                      Configuration updated
                    </Text>
                  : hasActionableSuggestions(msg.parsedConfig) ?
                    <View mt="md">
                      <Button
                        variant="primary"
                        variantColor="blue"
                        size="lg"
                        label={isApplyingAll ? "Applying changes..." : "Apply changes"}
                        onPress={() => handleApplyAll(msg.parsedConfig ?? undefined, idx)}
                        disabled={isApplyingAll}
                        loading={isApplyingAll}
                        loadingText="Applying changes..."
                      />
                    </View>
                  : null}
                </View>
              )}
            </View>
          );
        })}

        {/* Thinking indicator */}
        {isThinking && (
          <View alignItems="flex-start" mb="4xl">
            <View flexDirection="row" alignItems="center" gap="sm" mb="xs" style={{ alignSelf: "flex-start" }}>
              <AiIconSm size={28} />
              <Text textStyle="md/regular" fontWeight="600">
                AI Assistant
              </Text>
            </View>
            <View bg="background" borderWidth={1} borderColor="border" rounded={14} px="4xl" py="3xl" maxWidth="90%">
              <TypingAnimation dotSize={10} />
            </View>
          </View>
        )}
      </ScrollView>

      {/* Composer */}
      <AnimatedView
        style={[
          {
            paddingHorizontal: theme.spacing["5xl"],
            paddingBottom: theme.spacing["7xl"],
            paddingTop: theme.spacing["md"],
            borderTopWidth: 1,
            borderColor: theme.colors.border,
            backgroundColor: theme.colors.background,
          },
          animatedStyle,
        ]}
      >
        <FormInput
          value={draft}
          onChangeText={setDraft}
          placeholder="Tell me what you'd like to change..."
          title=""
          size="large"
          returnKeyType="send"
          onSubmitEditing={handleSend}
          editable={!isThinking}
        />
      </AnimatedView>
    </View>
  );
}

export function EmbeddedAgentScreen() {
  return <AgentScreenContent embeddedOverride />;
}

export default function AgentScreen() {
  return <AgentScreenContent />;
}
